
import Vue from 'vue';
import { directive as onClickaway } from 'vue-clickaway';
import { CandidateStudyProgram } from '~/services/CSPService';
import BackgroundProcessesService from '~/services/BackgroundProcessesService';
import URLS from '~/URLS';
import { HAS_MY_PROFILE } from '~/router';
import { SchoolVerification, SchoolVerificationState } from '~/services/SchoolService';
import { getAccessByRefresh } from '~/store/middlewares/refreshToken';

export default Vue.extend({
    name: 'SchoolVerificationIndicator',
    directives: { onClickaway },
    data() {
        return {
            infoDialogVisible: false,
        };
    },
    computed: {
        studyPrograms(): CandidateStudyProgram[] {
            return this.$store.getters['profileModule/getStudyPrograms']();
        },
        schoolVerifications(): SchoolVerification[] {
            return this.$interfaces.university.connection.get({ reactive: true }).data || [];
        },
        hasVerificationIssue(): boolean {
            return this.hasMissingVerification || this.hasPendingVerification;
        },
        hasMissingVerification(): boolean {
            return this.studyPrograms.some(
                x =>
                    (!x.verification ||
                        x.verification.state === SchoolVerificationState.needVerification) &&
                    x.studyProgram &&
                    x.studyProgram.school
            );
        },
        hasPendingVerification(): boolean {
            return this.studyPrograms.some(
                x => x.verification && x.verification.state === SchoolVerificationState.pending
            );
        },
        icon(): string {
            return this.hasMissingVerification ? 'circle-exclamation' : 'triangle-exclamation';
        },
        style(): string {
            return this.hasMissingVerification ? 'danger' : 'warning';
        },
        urls(): Record<string, string | null> {
            const urls: typeof URLS = (this as any).$URLS;
            const v = (cond: boolean, url: string) => (!cond ? null : url);

            return {
                myProfile: v(HAS_MY_PROFILE, urls.spotlight),
            };
        },
        headingTranslationKey(): string {
            return this.buildTranslationKey(
                this.hasMissingVerification ? 'needVerification.heading' : 'pending.heading'
            );
        },
        linkTranslationKey(): string {
            return this.buildTranslationKey(
                this.hasMissingVerification ? 'needVerification.linkText' : 'pending.linkText'
            );
        },
    },
    methods: {
        showInfo(): void {
            this.infoDialogVisible = true;
        },
        hideInfo(): void {
            this.infoDialogVisible = false;
        },
        buildTranslationKey(key: string): string {
            return `shared.schoolVerification.indicator.${key}`;
        },
        handleChangedVerifications(
            newVerifications: SchoolVerification[],
            oldVerifications: SchoolVerification[]
        ): void {
            const newVerifiedSchools = newVerifications
                .filter(x => x.state === SchoolVerificationState.verified)
                .map(x => x.schoolId);
            const oldVerifiedSchools = oldVerifications
                .filter(x => x.state === SchoolVerificationState.verified)
                .map(x => x.schoolId);

            if (
                newVerifiedSchools.length !== oldVerifiedSchools.length ||
                newVerifiedSchools.some(x => !oldVerifiedSchools.includes(x))
            ) {
                getAccessByRefresh();
                return;
            }
        },
    },
    watch: {
        schoolVerifications(newValue, oldValue): void {
            this.$store.commit('profileModule/setSchoolVerifications', newValue);
            this.handleChangedVerifications(newValue || [], oldValue || []);
        },
    },
    mounted() {
        BackgroundProcessesService.startVerificationPing();
    },
});
